/**
 * shopeeImport.js — logik Import Products untuk SHOPEE (tab Shopee dalam modal import).
 *
 * Fail sendiri (prinsip 3): tiada satu baris pun logik import TikTok diubah untuk
 * ciri ini. Komponen `ImportShopeeV39.jsx` = rupa; fail ini = peraturan + tulisan.
 *
 * ⭐ BENTUK ITEM SHOPEE dalam My Products (keputusan reka bentuk 3 Okt 2026):
 *    • `link`             = ''  ← SENGAJA KOSONG. `link` kad dihantar kepada robot
 *                               TikTok (Showcase + beg kuning — lihat pautanProduk.js).
 *                               Link Shopee di situ = robot TikTok cuba pasang produk
 *                               Shopee. Kosong = robot TikTok melangkau produk, sama
 *                               seperti entry manual tanpa link.
 *    • `shopeeProductUrl` = halaman produk Shopee (bentuk kanonik) — MEDAN BAHARU;
 *                               sumber ID Shopee, tanda platform, dan "Product ID"
 *                               dalam `evaluateProductData`.
 *    • `shopeeLink`       = link affiliate pendek (medan SEDIA ADA — dibawa ke kad
 *                               Workspace oleh App.jsx dan dipakai robot FB Reels).
 *    Produk "ada kedua-dua" = item TikTok (`link` berisi) yang turut ada `shopeeLink`.
 *
 * ⚠️ KEMBAR: `idShopeeDariUrl` / `urlProdukShopee` / pola link juga wujud dalam extension
 *    (`sesaat (extension)/src/chrome/shopee/shopeeScrape.ts` + `shopeeAffiliate.ts`).
 *    Ubah satu, ubah dua-dua.
 * ⚠️ KEMBAR: bentuk payload item dalam `simpanImportShopee` = bentuk `handleImportSave`
 *    (LibraryView.jsx, import TikTok). Medan baharu pada item import → tambah di KEDUA-DUA.
 */
import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { buildDataProdukBlock } from '../services/productReadyService';

export const SUB_ID_LALAI = 'sesaat';
export const MAKS_SUB_ID = 50;
export const MAKS_LINK_SEPUSINGAN = 5;      // had borang Custom Link Shopee (diukur 3 Okt 2026)
const KUNCI_SUB_ID = 'sesaat_shopee_subid_v1';
const KUNCI_AFFILIATE = 'sesaat_shopee_auto_affiliate_v1';

// ★ MULTI-NEGARA (2026-10-27) — dahulu Shopee Malaysia sahaja; pelanggan Singapura (shopee.sg,
//   harga "$") langsung tak boleh import. ⚠️ KEMBAR: senarai domain + pemetaan negara juga dalam
//   extension `src/chrome/shopee/shopeeScrape.ts` (+ storefront + affiliate). Ubah satu, ubah SEMUA.
export const DOMAIN_SHOPEE = [
    'shopee.com.my', 'shopee.sg', 'shopee.co.id', 'shopee.co.th',
    'shopee.ph', 'shopee.vn', 'shopee.tw', 'shopee.com.br',
];
const DOMAIN_ALT = DOMAIN_SHOPEE.map((d) => d.replace(/\./g, '\\.')).join('|');
/** Kod negara `shop_location` ("SG") → domain kedai. */
export const NEGARA_KE_DOMAIN = {
    MY: 'shopee.com.my', SG: 'shopee.sg', ID: 'shopee.co.id', TH: 'shopee.co.th',
    PH: 'shopee.ph', VN: 'shopee.vn', TW: 'shopee.tw', BR: 'shopee.com.br',
};
/** Domain kedai Shopee yang disokong dalam URL/hostname, atau '' bila bukan Shopee. */
export function domainShopee(url) {
    const m = String(url || '').match(new RegExp('(?:^|//|\\.)(' + DOMAIN_ALT + ')(?:[/?#:]|$)', 'i'));
    return m ? m[1].toLowerCase() : '';
}

const HOS_PRODUK = new RegExp('^https?://(?:www\\.)?(?:' + DOMAIN_ALT + ')/', 'i');
const HOS_PENDEK = new RegExp('^https?://(?:s\\.(?:' + DOMAIN_ALT + ')|shope\\.ee|(?:[a-z]{2}\\.)?shp\\.ee)/', 'i');
const POLA_LINK_PENDEK = new RegExp('^https://(?:s\\.(?:' + DOMAIN_ALT + ')|shope\\.ee|(?:[a-z]{2}\\.)?shp\\.ee)/[A-Za-z0-9_-]{4,40}$');
const POLA_URL_KANONIK = new RegExp('^https://(?:' + DOMAIN_ALT + ')/product/\\d{1,20}/\\d{1,20}$');

/** Link Shopee (halaman produk ATAU link pendek) — mana-mana negara yang disokong? */
export function adakahLinkShopee(url) {
    const u = String(url || '').trim();
    return HOS_PRODUK.test(u) || HOS_PENDEK.test(u);
}

/** {shopId,itemId,domain} dari URL produk Shopee — bentuk `…-i.<shop>.<item>` dan `/product/<shop>/<item>`. */
export function idShopeeDariUrl(url) {
    const u = String(url || '');
    if (!HOS_PRODUK.test(u)) return null;
    const laluan = u.split(/[?#]/)[0];
    const m = laluan.match(/-i\.(\d{1,20})\.(\d{1,20})$/) || laluan.match(/\/product\/(\d{1,20})\/(\d{1,20})\/?$/);
    return m ? { shopId: m[1], itemId: m[2], domain: domainShopee(u) || 'shopee.com.my' } : null;
}

export function urlProdukShopee(shopId, itemId, domain = 'shopee.com.my') {
    const d = DOMAIN_SHOPEE.includes(domain) ? domain : 'shopee.com.my';
    return `https://${d}/product/${shopId}/${itemId}`;
}

/** Kunci unik produk Shopee ("<shopId>.<itemId>") — untuk semakan "DAH ADA". '' bila tiada. */
export function kunciShopee(url) {
    const id = idShopeeDariUrl(url);
    return id ? `${id.shopId}.${id.itemId}` : '';
}

/**
 * Pecah teks tampalan → senarai link Shopee yang unik.
 *
 * ★ Arahan user 3 Okt 2026: JANGAN bergantung pada nombor ID dalam link. APA-APA link
 *   Shopee Malaysia diterima (link penuh, link pendek, bentuk URL yang belum pernah kita
 *   lihat) — robot membukanya seadanya dan mengambil ID dari data halaman. Link yang
 *   rupanya bukan halaman produk gagal dengan ayat jelas pada langkah Scrape.
 *   ID dalam link (bila ada) hanya dipakai untuk menyahpendua dua bentuk URL produk sama.
 * @returns {{ links: string[], bukanShopee: number }}
 */
export function parseLinksShopee(teks) {
    // Dipecah ikut RUANG/BARIS sahaja — koma sah dalam URL (nama produk boleh mengandunginya).
    const keping = String(teks || '').split(/\s+/).map((s) => s.trim().replace(/^[<("']+|[>)"',;]+$/g, '')).filter(Boolean);
    const dilihat = new Set();
    const links = [];
    let bukanShopee = 0;
    for (let k of keping) {
        // Tampalan tanpa "https://" (cth disalin dari bar alamat telefon).
        if (new RegExp('^(?:www\\.)?(?:(?:s\\.)?(?:' + DOMAIN_ALT + ')|shope\\.ee|(?:[a-z]{2}\\.)?shp\\.ee)/', 'i').test(k)) k = `https://${k}`;
        if (!/^https?:\/\//i.test(k)) continue;
        if (!adakahLinkShopee(k)) { bukanShopee++; continue; }
        const id = idShopeeDariUrl(k);
        const kunci = id ? `${id.shopId}.${id.itemId}` : k;
        if (dilihat.has(kunci)) continue;
        dilihat.add(kunci);
        links.push(k);
    }
    return { links, bukanShopee };
}

/* ── Storefront affiliate (collshp.com/<nama>) ── */
export const MAKS_PRODUK_STOREFRONT = 300;   // ⚠️ KEMBAR: extension shopeeStorefront.ts
export const LALAI_PRODUK_STOREFRONT = 40;
const POLA_STOREFRONT = /^https:\/\/collshp\.com\/[A-Za-z0-9._-]{1,80}\/?(?:[?#].*)?$/;

/** Link storefront yang sah (dikemas) atau '' — menerima tampalan tanpa "https://". */
export function urlStorefrontShopee(teks) {
    let u = String(teks || '').trim();
    if (!u) return '';
    if (/^collshp\.com\//i.test(u)) u = `https://${u}`;
    u = u.replace(/^http:\/\//i, 'https://');
    return POLA_STOREFRONT.test(u) ? u : '';
}

/* ── Storefront TERSIMPAN (dropdown dalam Scrape Storefront) ──
   Disimpan pada akaun: `users/{uid}.shopeeStorefronts` = [{id, nama, url}] — corak SAMA
   dengan `libraryGroups` / `tiktokProfiles` (senarai kecil BERHAD dalam doc user; had di
   bawah menghalang ia membesar tanpa had). Setiap tulisan melalui `kemasStorefrontTersimpan`
   supaya nilai rosak tidak pernah sampai ke Firestore. */
export const MAKS_STOREFRONT_TERSIMPAN = 20;
export const MAKS_NAMA_STOREFRONT = 40;

/** Nama lalai dari link: "https://collshp.com/foodieshopee?x" → "foodieshopee". */
export function namaDariUrlStorefront(url) {
    const m = String(url || '').match(/^https:\/\/collshp\.com\/([A-Za-z0-9._-]{1,80})/);
    return m ? m[1] : '';
}

/** Senarai storefront yang sah sahaja: link sah, nama dikemas, id + link unik, dipotong ke had. */
export function kemasStorefrontTersimpan(senarai) {
    const idDilihat = new Set();
    const urlDilihat = new Set();
    const keluar = [];
    for (const s of (Array.isArray(senarai) ? senarai : [])) {
        const url = urlStorefrontShopee(s?.url);
        const id = String(s?.id || '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 24);
        if (!url || !id || idDilihat.has(id) || urlDilihat.has(url.toLowerCase())) continue;
        idDilihat.add(id);
        urlDilihat.add(url.toLowerCase());
        const nama = String(s?.nama || '').replace(/\s+/g, ' ').trim().slice(0, MAKS_NAMA_STOREFRONT) || namaDariUrlStorefront(url);
        keluar.push({ id, nama, url });
        if (keluar.length >= MAKS_STOREFRONT_TERSIMPAN) break;
    }
    return keluar;
}

/** Bilangan produk storefront: nombor bulat 1..MAKS (lalai bila kosong/pelik). */
export function bilanganStorefront(nilai) {
    const n = parseInt(String(nilai ?? '').trim(), 10);
    if (!Number.isFinite(n) || n < 1) return LALAI_PRODUK_STOREFRONT;
    return Math.min(MAKS_PRODUK_STOREFRONT, n);
}

/**
 * Tapis senarai produk yang dipulangkan extension (prinsip 4 — jangan percaya data):
 * hanya entri dengan shopId + itemId berdigit; nyahpendua; medan dikemas.
 */
export function sahkanProdukStorefront(senarai) {
    const dilihat = new Set();
    const keluar = [];
    for (const p of (Array.isArray(senarai) ? senarai : [])) {
        const shopId = String(p?.shopId ?? '');
        const itemId = String(p?.itemId ?? '');
        if (!/^\d{1,20}$/.test(shopId) || !/^\d{1,20}$/.test(itemId)) continue;
        const kunci = `${shopId}.${itemId}`;
        if (dilihat.has(kunci)) continue;
        dilihat.add(kunci);
        // Negara produk: extension sudah memetakan (`domain`); sandaran ikut `location`; akhir MY.
        const domain = DOMAIN_SHOPEE.includes(String(p?.domain))
            ? String(p.domain)
            : (NEGARA_KE_DOMAIN[String(p?.location || '').toUpperCase()] || 'shopee.com.my');
        keluar.push({
            kunci,
            url: urlProdukShopee(shopId, itemId, domain),
            nama: String(p?.nama || '').trim().slice(0, 300) || kunci,
            harga: String(p?.harga || '').slice(0, 40),
            soldOut: p?.soldOut === true,
        });
    }
    return keluar;
}

/** Sub ID: huruf + nombor sahaja (peraturan borang Shopee), dipotong ke had. */
export function bersihSubId(nilai) {
    return String(nilai ?? '').replace(/[^A-Za-z0-9]/g, '').slice(0, MAKS_SUB_ID);
}

/* Tetapan per-BROWSER. ⛔ `tulis…` dipanggil dari PENGENDALI (onChange/klik) sahaja,
   bukan dari useEffect — effect turut berjalan semasa mount dan akan menyimpan nilai
   lalai (perangkap yang sama seperti `sesaat_beg_kuning_v1`). */
export function bacaSubIdTersimpan() {
    try { const v = localStorage.getItem(KUNCI_SUB_ID); return v === null ? SUB_ID_LALAI : bersihSubId(v); } catch { return SUB_ID_LALAI; }
}
export function tulisSubIdTersimpan(nilai) {
    try { localStorage.setItem(KUNCI_SUB_ID, bersihSubId(nilai)); } catch { /* storan ditutup — abai */ }
}
export function bacaAutoAffiliate() {
    try { return localStorage.getItem(KUNCI_AFFILIATE) !== '0'; } catch { return true; }
}
export function tulisAutoAffiliate(hidup) {
    try { localStorage.setItem(KUNCI_AFFILIATE, hidup ? '1' : '0'); } catch { /* storan ditutup — abai */ }
}

/**
 * Buang ULASAN hasil scrape daripada data produk Shopee (keputusan user 2026-10-06: "buang").
 * Sebab: pembaca ulasan Shopee dalam extension mencantum balasan PENJUAL ("Seller's Response: …")
 * ke dalam teks ulasan, lalu ia disuap kepada AI sebagai "ulasan pelanggan sebenar" (bahan hook).
 * Ulasan bukan syarat READY, jadi ia dibuang terus di PINTU MASUK webapp (mesej kemajuan scrape) —
 * berkesan serta-merta untuk SEMUA versi extension di tangan pelanggan, tanpa ZIP baharu.
 * ⚠️ Extension masih meluangkan beberapa saat mengumpul ulasan yang kemudian dibuang di sini —
 *    buang langkah itu dalam `shopeeScrape.ts` bila ZIP seterusnya dibina.
 * Ulasan yang pelanggan TAIP SENDIRI dalam "Edit Manual" tidak melalui fungsi ini dan kekal.
 * Memulangkan salinan; objek asal tidak diubah.
 */
export function buangUlasanScrape(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data) || !('topReviews' in data)) return data;
    const salinan = { ...data };
    delete salinan.topReviews;
    return salinan;
}

/** Platform sesuatu item My Products — untuk tanda pada baris/kad. */
export function platformItem(item) {
    const p = item || {};
    return {
        tiktok: /tiktok\.com/i.test(String(p.link || '')),
        shopee: !!(String(p.shopeeProductUrl || '').trim() || String(p.shopeeLink || '').trim()),
    };
}

/* ─────────────────────────────────────────────────────────────────────────────
   TUKAR LINK AFFILIATE — pecah kepada kumpulan 5, padankan hasil ke produk.

   `hantar(links, subId)` = satu pusingan borang (extension; pulang
   `{ok:true, links}` atau `{ok:false, kod, error, barisRosak?}` atau null bila
   extension tidak menjawab).

   Peraturan padanan (aduan user: "takut tersalah masukkan link dalam card"):
     • Extension sudah menjamin bilangan keluar = bilangan masuk; di sini disemak
       SEKALI LAGI sebelum dipadankan ikut kedudukan.
     • Shopee menolak SELURUH kumpulan bila satu link rosak dan menyebut nombor
       barisnya → baris itu ditanda gagal, bakinya dicuba semula (sekali setiap
       baris rosak, jadi paling banyak 5 pusingan tambahan setiap kumpulan).
     • Ralat yang bukan salah link (login, masa tamat) → hentikan SEMUA kumpulan
       seterusnya; tiada guna mengetuk Shopee 8 kali untuk ralat yang sama.
   ───────────────────────────────────────────────────────────────────────────── */
export async function tukarAffiliateBerkumpulan({ urls, subId, hantar, padaKemajuan, patutBerhenti }) {
    const senarai = Array.from(new Set((Array.isArray(urls) ? urls : []).filter((u) => POLA_URL_KANONIK.test(String(u || '')))));
    const hasil = {};     // url → link affiliate
    const gagal = {};     // url → sebab
    let siap = 0;
    let ralatUmum = '';

    // ★ Kumpul ikut NEGARA — portal Custom Link berasingan per region (affiliate.shopee.sg vs
    //   affiliate.shopee.com.my). Satu pusingan borang WAJIB satu negara (lihat shopeeAffiliate.ts).
    const kumpulanNegara = new Map();
    for (const u of senarai) {
        const d = domainShopee(u) || 'shopee.com.my';
        if (!kumpulanNegara.has(d)) kumpulanNegara.set(d, []);
        kumpulanNegara.get(d).push(u);
    }

    for (const senaraiNegara of kumpulanNegara.values()) {
      if (ralatUmum || patutBerhenti?.()) break;
      for (let mula = 0; mula < senaraiNegara.length; mula += MAKS_LINK_SEPUSINGAN) {
        if (patutBerhenti?.()) { ralatUmum = ralatUmum || 'Dihentikan.'; break; }
        let kumpulan = senaraiNegara.slice(mula, mula + MAKS_LINK_SEPUSINGAN);
        // Paling banyak satu percubaan tambahan bagi setiap baris yang Shopee tolak.
        for (let pusingan = 0; pusingan <= MAKS_LINK_SEPUSINGAN && kumpulan.length > 0; pusingan++) {
            const r = await hantar(kumpulan, subId);
            if (r && r.ok === true && Array.isArray(r.links) && r.links.length === kumpulan.length
                && r.links.every((l) => POLA_LINK_PENDEK.test(String(l || '')))) {
                kumpulan.forEach((u, i) => { hasil[u] = r.links[i]; });
                kumpulan = [];
                break;
            }
            const sebab = !r ? 'Tiada respons dari extension — pastikan ia aktif.'
                : (r.ok === true ? 'Hasil Shopee tidak sepadan dengan bilangan produk — dibuang.' : (r.error || 'Gagal menukar link.'));
            const baris = r && r.ok === false && r.kod === 'ditolak' && Number.isInteger(r.barisRosak) ? r.barisRosak : 0;
            if (baris >= 1 && baris <= kumpulan.length) {
                gagal[kumpulan[baris - 1]] = sebab;
                kumpulan = kumpulan.filter((_, i) => i !== baris - 1);
                continue;   // cuba baki kumpulan tanpa baris rosak
            }
            kumpulan.forEach((u) => { gagal[u] = sebab; });
            kumpulan = [];
            // Ralat yang akan berulang pada setiap kumpulan → berhenti terus.
            if (!r || r.ok === true || ['login', 'borang', 'masa', 'tab', 'input', 'kiraan'].includes(r.kod)) ralatUmum = sebab;
        }
        // Had pusingan habis sedangkan masih ada baki — tanda gagal, jangan tinggal tergantung.
        kumpulan.forEach((u) => { if (!gagal[u]) gagal[u] = 'Gagal menukar link selepas beberapa percubaan.'; });
        siap += Math.min(MAKS_LINK_SEPUSINGAN, senaraiNegara.length - mula);
        padaKemajuan?.({ siap, jumlah: senarai.length });
        if (ralatUmum) break;
      }
    }
    // Baki yang belum sempat (ralat berulang / dihentikan) ditandai gagal — tiada tergantung.
    if (ralatUmum) senarai.forEach((u) => { if (!hasil[u] && !gagal[u]) gagal[u] = ralatUmum; });
    return { hasil, gagal, ralatUmum };
}

/* ─────────────────────────────────────────────────────────────────────────────
   SIMPAN — tulis item hasil scrape Shopee ke My Products.

   Nilai disemak SEBELUM ditulis (prinsip 4): nama + gambar + URL produk kanonik
   WAJIB sah; link affiliate ditulis hanya bila berbentuk link pendek Shopee.
   Item yang tidak lulus DILANGKAU dan dikira dalam `ditolak` — pemanggil wajib
   memaparkannya (jangan senyap).
   ───────────────────────────────────────────────────────────────────────────── */
export async function simpanImportShopee({ folderId, items, nomborMaks = 0, email, profileId }) {
    if (!folderId || !email) throw new Error('Maklumat tidak lengkap untuk menyimpan.');
    const senarai = Array.isArray(items) ? items : [];
    const sah = [];
    const ditolak = [];
    for (const it of senarai) {
        // JSON round-trip = buang `undefined` (Firestore tolak) + nilai bukan-JSON.
        const sd = JSON.parse(JSON.stringify(it?.data || {}));
        const url = String(it?.shopeeProductUrl || '').trim();
        const nama = String(sd.name || '').trim();
        const gambar = String(sd.imageUrl || (Array.isArray(sd.gallery) && sd.gallery[0]) || '').trim();
        if (!POLA_URL_KANONIK.test(url)) { ditolak.push({ nama, sebab: 'URL produk Shopee tidak sah' }); continue; }
        if (!nama) { ditolak.push({ nama: url, sebab: 'Nama produk kosong' }); continue; }
        if (!/^https:\/\//.test(gambar)) { ditolak.push({ nama, sebab: 'Gambar produk tiada' }); continue; }
        const aff = String(it?.shopeeLink || '').trim();
        sah.push({ it, sd, url, nama, gambar, shopeeLink: POLA_LINK_PENDEK.test(aff) ? aff : '' });
    }

    const itemsRef = collection(db, 'library', folderId, 'items');
    const asas = Number.isFinite(nomborMaks) ? nomborMaks : 0;
    const CHUNK = 400;   // writeBatch had 500 ops
    let saved = 0;
    for (let mula = 0; mula < sah.length; mula += CHUNK) {
        const batch = writeBatch(db);
        sah.slice(mula, mula + CHUNK).forEach((s, j) => {
            const payload = {
                link: '',                        // ⛔ sengaja kosong — lihat nota kepala fail
                shopeeProductUrl: s.url,
                shopeeLink: s.shopeeLink,
                group: 'own',
                scrapedData: s.sd,
                name: s.nama,
                imageUrl: s.gambar,
                number: asas + mula + j + 1,
                customGroup: s.it.groupId ?? null,
                price: s.sd.price ?? '',
                originalPrice: s.sd.originalPrice ?? '',
                discount: s.sd.discount ?? '',
                addedAt: serverTimestamp(),
                addedBy: email,
                updatedAt: serverTimestamp(),
            };
            if (profileId) payload.profileId = profileId;
            if (s.it.ready) {
                payload.hasIntelligence = true;
                payload.intelligenceJson = {
                    imageAnalysis: buildDataProdukBlock(s.sd),
                    webResearch: '',
                    generatedAt: serverTimestamp(),
                    generatedBy: email,
                    sourceType: 'scrape-only',
                };
                payload.pendingReason = null;
            } else {
                payload.hasIntelligence = false;
                payload.pendingReason = s.it.pendingReason || 'Data tidak lengkap';
            }
            batch.set(doc(itemsRef), payload);
        });
        await batch.commit();
        saved += Math.min(CHUNK, sah.length - mula);
    }
    return { saved, ditolak };
}
